import { Button, Dropdown } from '@mairistem/composants';
import { Icon } from '@mairistem/finances-utils';
import React from 'react';
import PerimetreIdee, {
	PERIMETRES_IDEE,
} from '../../../../../constants/simulation/perimetreIdee';
import type {
	default as IdeeImportable,
	ValeursPerimetresIdee,
} from '../../../../../interfaces/simulation/ideeImportable';
import {
	compterIdeesParPerimetre,
	formatNombreIdees,
	getAidePerimetre,
	getLibelleBoutonPerimetre,
	getLibelleChampPerimetre,
	getPerimetreSuivant,
	getTitrePerimetre,
	getValeurPerimetre,
} from '../../../../../utils/simulation/perimetreIdee';

interface ImportIdeePerimetreProps {
	// Toutes les idees eligibles, quel que soit leur cran
	idees: IdeeImportable[];
	onChange: (perimetre: PerimetreIdee) => void;
	perimetre: PerimetreIdee;
	valeurs: ValeursPerimetresIdee;
}

const ImportIdeePerimetre = ({
	idees,
	onChange,
	perimetre,
	valeurs,
}: ImportIdeePerimetreProps) => {
	const suivant = getPerimetreSuivant(perimetre);
	// Decomptes de chaque cran, recalcules seulement quand la liste change
	const nombres = React.useMemo(() => compterIdeesParPerimetre(idees), [idees]);

	return (
		<div className="importIdeePerimetre">
			<div className="importIdeePerimetreCommande">
				<Dropdown
					className="importIdeePerimetreChamp"
					data-testid="importIdeePerimetreChamp"
					fluid
					onChange={(_e, { value }) => onChange(value as PerimetreIdee)}
					options={PERIMETRES_IDEE.map((option, niveau) => ({
						key: option,
						value: option,
						// Le decompte s'affiche a droite de l'option
						description: formatNombreIdees(nombres[option]),
						content: (
							<div
								className="importIdeePerimetreOption"
								data-testid={`importIdeePerimetreOption-${option}`}
								// Options indentees de cran en cran pour montrer l'elargissement
								style={{ paddingLeft: niveau * 12 }}
							>
								<span className="importIdeePerimetreCoche">
									{option === perimetre && (
										<Icon iconSet="Lucide" name="Check" size={14} />
									)}
								</span>
								<span>
									<strong>{getTitrePerimetre(option)}</strong>
									<br />
									{getValeurPerimetre(option, valeurs)}
								</span>
							</div>
						),
					}))}
					selection
					trigger={
						<span className="importIdeePerimetreResume">
							<Icon iconSet="Lucide" name="Funnel" size={15} />
							<strong data-testid="importIdeePerimetreLibelle">
								{getLibelleChampPerimetre(perimetre, valeurs)}
							</strong>
							<span>{formatNombreIdees(nombres[perimetre])}</span>
						</span>
					}
					value={perimetre}
				/>
				{/* Toujours actif, meme quand le cran suivant n'apporte aucune idee ;
				au dernier cran, il ramene au premier */}
				<Button
					basic
					className="importIdeePerimetreBouton"
					data-testid="importIdeePerimetreBouton"
					onClick={() => onChange(suivant ?? PerimetreIdee.ARTICLE)}
				>
					<Icon
						iconSet="Lucide"
						name={suivant ? 'Maximize2' : 'Minimize2'}
						size={14}
					/>
					{getLibelleBoutonPerimetre(perimetre, nombres)}
				</Button>
			</div>
			<p data-testid="importIdeePerimetreAide">
				{perimetre === PerimetreIdee.TOUTES && (
					<span data-testid="importIdeePerimetreAvertissement">
						<Icon iconSet="Lucide" name="TriangleAlert" size={14} />{' '}
					</span>
				)}
				{getAidePerimetre(perimetre, valeurs)}
			</p>
		</div>
	);
};

export default ImportIdeePerimetre;
